import type { BoardHold } from './climb';

/**
 * The board dashboard's view of a problem: counts rather than the climber-facing
 * payload the member board tab uses. See
 * `Api::Gyms::BoardDashboardClimbsController` for why the two are separate.
 */
export interface BoardDashboardClimb {
  id: string;
  slug: string;
  name: string;
  /** Carried on the row so the panel can edit a problem without a second fetch. */
  description: string | null;
  grade: { id: string; name: string; value: number } | null;
  /** The angle it is graded at; null on a board with no angle to speak of. */
  inclination: number | null;
  adjustable_inclination: boolean;
  circuit: boolean;
  /** The circuit also says which hand takes each hold. Circuits only. */
  forced_hands: boolean;
  /** The grid cells, so the board photo can draw the problem without a fetch. */
  holds: BoardHold[];
  holds_count: number;
  created_at: string | null;
  sends_count: number;
  attempts_count: number;
  rating_avg: number | null;
  ratings_count: number;
  /** How many times the problem was put up on the wall. */
  lightings_count: number;
  shares_count: number;
  route_setter_name: string | null;
  /**
   * The same problem at other angles. Present on a list row, absent on the
   * variants themselves — they are leaves.
   */
  variants?: BoardDashboardClimb[];
  variants_count?: number;
}

/**
 * What an edit may change about a problem already on the wall. Not the board it
 * is on, not who set it and not its confirmation state: those are moves rather
 * than corrections, and each has its own way in.
 *
 * `holds` is left out when the selection was not touched — the server only
 * re-reads it off the wall, and re-runs the rules on it, when it is sent.
 */
export interface BoardDashboardClimbPayload {
  name: string;
  grade_id: string;
  description?: string | null;
  circuit?: boolean;
  /** Dropped server-side on a problem that is not a circuit. */
  forced_hands?: boolean;
  /** Only meaningful on a board type that grades per angle; ignored elsewhere. */
  inclination?: number | null;
  holds?: BoardHold[];
}

/** Who did something, as the history panels show them. */
export interface BoardDashboardUser {
  id: string;
  name: string;
  username: string | null;
  slug: string;
  avatar_small_url: string | null;
}

export interface BoardDashboardAscent {
  id: string;
  topped: boolean;
  sent_at: string | null;
  logged_at: string;
  attempts_count: number | null;
  send_type: string | null;
  rating: number | null;
  user: BoardDashboardUser | null;
}

export interface BoardDashboardAttempt {
  id: string;
  attempt_type: string | null;
  attempted_at: string;
  user: BoardDashboardUser | null;
}

export interface BoardDashboardLighting {
  id: string;
  lit_at: string;
  /** Null when nobody was signed in at the wall. */
  user: BoardDashboardUser | null;
}

export interface BoardDashboardShare {
  id: string;
  shared_at: string;
  template: string | null;
  media_type: string | null;
  user: BoardDashboardUser | null;
}

/** One problem's whole history — what the detail modal reads. */
export interface BoardDashboardClimbDetail {
  climb: BoardDashboardClimb;
  ascents: BoardDashboardAscent[];
  attempts: BoardDashboardAttempt[];
  lightings: BoardDashboardLighting[];
  shares: BoardDashboardShare[];
}

export type BoardDashboardSort =
  | 'created_at'
  | 'name'
  | 'grade'
  | 'sends'
  | 'attempts'
  | 'rating'
  | 'lightings'
  | 'shares';

export interface BoardDashboardQuery {
  page?: number;
  per_page?: number;
  name?: string;
  grade_min?: string;
  grade_max?: string;
  inclination?: number;
  circuit?: 'true' | 'false';
  created_from?: string;
  created_to?: string;
  sort?: BoardDashboardSort;
  direction?: 'asc' | 'desc';
}

export interface BoardDashboardPage {
  climbs: BoardDashboardClimb[];
  hasMore: boolean;
  total: number;
  /** Every angle the board holds a problem at, not only this page's. */
  inclinations: number[];
}

/** What the filter bar holds; the query above is built from it. */
export interface BoardDashboardFilters {
  name: string;
  gradeMin: string | null;
  gradeMax: string | null;
  inclination: number | null;
  circuit: 'all' | 'yes' | 'no';
  createdFrom: string | null;
  createdTo: string | null;
  sort: BoardDashboardSort;
  direction: 'asc' | 'desc';
}

export const DEFAULT_BOARD_DASHBOARD_FILTERS: BoardDashboardFilters = {
  name: '',
  gradeMin: null,
  gradeMax: null,
  inclination: null,
  circuit: 'all',
  createdFrom: null,
  createdTo: null,
  sort: 'created_at',
  direction: 'desc',
};
